import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";
import { useLang } from "@/lib/i18n";

const CONTENT = {
  fr: {
    mentions: {
      title: "Mentions légales",
      seo: "Mentions légales de la Maison Partita.",
      body: [
        ["Éditeur", "Le présent site est édité par la Maison Partita. Les coordonnées complètes sont communiquées sur demande via le formulaire de contact."],
        ["Directeur de la publication", "Non communiqué."],
        ["Hébergement", "Le site et l'ensemble de ses fichiers, y compris les médias, sont servis depuis le domaine de la maison. Aucun lecteur tiers n'est utilisé."],
        ["Propriété intellectuelle", "Les textes, la typographie, les glyphes et les visuels sont la propriété de la maison. Toute reproduction est soumise à autorisation préalable."],
      ],
    },
    cgv: {
      title: "Conditions générales de vente",
      seo: "Conditions générales de vente de la Maison Partita.",
      body: [
        ["Prix", "Les prix affichés sont exprimés en euros, toutes taxes comprises. Aucun paiement n'est prélevé sur le site : le règlement est convenu par e-mail après confirmation de la disponibilité."],
        ["Références", "Les eaux de parfum de la maison sont des séries limitées numérotées. La plupart des références sont épuisées ou à venir ; leur disponibilité n'est pas garantie."],
        ["Commande", "La commande se passe depuis le panier : le client transmet sa sélection et ses coordonnées, la maison confirme la disponibilité sous 48 h ouvrées et adresse les modalités de règlement par e-mail. La vente n'est conclue qu'à réception du règlement. Les demandes particulières restent possibles via le formulaire de contact."],
        ["Livraison", "La livraison est offerte au-delà de 150 €. Des échantillons accompagnent chaque envoi."],
      ],
    },
    confidentialite: {
      title: "Confidentialité",
      seo: "Politique de confidentialité de la Maison Partita. Conforme au RGPD.",
      body: [
        ["Données collectées", "Seules les adresses e-mail transmises volontairement (lettre de la maison, alerte de retour, contact) sont conservées."],
        ["Finalité", "Ces données servent uniquement à répondre à ta demande ou à t'informer. Elles ne sont jamais cédées à un tiers."],
        ["Polices d'écriture", "Les polices sont auto-hébergées. Aucune requête n'est adressée à un service tiers ; ton adresse IP n'est transmise à personne."],
        ["Droits", "Tu peux demander l'accès, la rectification ou la suppression de tes données via le formulaire de contact."],
      ],
    },
    retours: {
      title: "Politique de retour",
      seo: "Politique de retour de la Maison Partita.",
      body: [
        ["Délai", "En cas de vente conclue avec la maison, un délai de rétractation de quatorze jours s'applique à compter de la réception."],
        ["Conditions", "Le flacon doit être retourné intact, dans son étui et sa série d'origine, non entamé."],
        ["Modalités", "Toute demande de retour se fait au préalable via le formulaire de contact."],
      ],
    },
  },
  en: {
    mentions: {
      title: "Legal notice",
      seo: "Legal notice of Maison Partita.",
      body: [
        ["Publisher", "This site is published by Maison Partita. Full contact details are provided on request via the contact form."],
        ["Publishing director", "Undisclosed."],
        ["Hosting", "The site and all of its files, media included, are served from the house's own domain. No third-party player is used."],
        ["Intellectual property", "The texts, typography, glyphs and visuals are the property of the house. Any reproduction is subject to prior authorisation."],
      ],
    },
    cgv: {
      title: "Terms of sale",
      seo: "Terms of sale of Maison Partita.",
      body: [
        ["Prices", "Prices are shown in euros, all taxes included. No payment is taken on the site: payment is agreed by e-mail once availability is confirmed."],
        ["References", "The house's eaux de parfum are numbered limited series. Most references are sold out or upcoming; their availability is not guaranteed."],
        ["Orders", "Orders are placed from the cart: the customer sends their selection and details, the house confirms availability within 48 working hours and sends the payment details by e-mail. The sale is only concluded once payment is received. Special requests remain possible via the contact form."],
        ["Delivery", "Delivery is complimentary above €150. Samples accompany every shipment."],
      ],
    },
    confidentialite: {
      title: "Privacy",
      seo: "Privacy policy of Maison Partita. GDPR compliant.",
      body: [
        ["Data collected", "Only e-mail addresses submitted voluntarily (house letter, return alert, contact) are kept."],
        ["Purpose", "This data is used solely to answer your enquiry or to keep you informed. It is never passed on to a third party."],
        ["Fonts", "Fonts are self-hosted. No request is sent to any third-party service; your IP address is transmitted to no one."],
        ["Your rights", "You may request access to, rectification or deletion of your data via the contact form."],
      ],
    },
    retours: {
      title: "Return policy",
      seo: "Return policy of Maison Partita.",
      body: [
        ["Period", "For any sale concluded with the house, a fourteen-day withdrawal period applies from the date of receipt."],
        ["Conditions", "The bottle must be returned intact, in its original case and series, unopened."],
        ["Procedure", "Any return request must first be made via the contact form."],
      ],
    },
  },
};

export default function Legal({ page }) {
  const lang = useLang();
  const c = CONTENT[lang][page];
  useSeo(`${c.title} — Maison Partita`, c.seo);
  return (
    <section className="max-w-2xl mx-auto px-5 sm:px-10 pt-28 pb-32">
      <Reveal>
        <h1 className="text-3xl sm:text-4xl tracking-[0.08em] text-zinc-50">{c.title}</h1>
        <div className="mt-14 space-y-12">
          {c.body.map(([h, t]) => (
            <div key={h}>
              <h2 className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{h}</h2>
              <p className="mt-4 text-[18px] leading-[1.8] text-zinc-300">{t}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
}
